import { ErrorMessages, isInvalidTokenError, ProviderError } from 'lib/AppErrors';
import AppEvents from 'lib/AppEvents';
import Constants from 'lib/Constants';
import { debug } from 'lib/utils/debug';
import { ProviderNames } from 'models/Providers';
import ApiService from 'services/ApiService';
import AuthService from 'services/AuthService';
import EventService from 'services/EventService';
import IDbOrCacheStore from 'stores/IDbOrCacheStore';
import IDbStore from 'stores/IDbStore';
import { getAccountProvider, getInnerError, handleResponseErrorNotifications, isAccountOwner } from 'utils/adaya/AdayaUtils';
import { getNormalizedUrlParams } from 'utils/BrowserUtils';
import LocalStorage from 'utils/LocalStorage';
import { getLogger } from 'utils/logging';
import NotificationService from '../NotificationService';

const { log, warn } = getLogger('facebook', { color: 'blue', enabled: false });

// Todo: use event bus to signal cancelling data request (ie. if user logs out)...

// this is for development, to force the data window to move (note it may not work as intended...)
const DAY_OFFSET = 0;
const DATE_FORMAT = 'YYYY-MM-DD';
const POLL_TIME_MS = 1000;

const isOverLimitError = (message) => {
    return message.indexOf('too many calls') > 0 || message.indexOf('Please reduce the amount') > 0;
};

class FacebookService {
    private tooManyCalls = false;
    private store: IDbStore;
    private killPolling = false; // set when user logs out, for instance.

    constructor() {
        this.store = new IDbOrCacheStore('FB_');
        this.bindEvents();

        // hack to mock data for dev
        //LocalStorage.remove(Constants.USE_MOCK_KEYS);
        const params = getNormalizedUrlParams(); // new URLSearchParams(window.location.search);
        if (params.get('dev') || process.env.FB_USE_MOCK_DATA == 'true') {
            LocalStorage.set(Constants.USE_MOCK_KEYS, true);
            log('USING MOCK FB ACCESS TOKEN.');
        } else if (params.get('dev') == 'false') {
            LocalStorage.remove(Constants.USE_MOCK_KEYS);
            log('REMOVING MOCK FB ACCESS TOKEN.');
        }
    }

    public hasMockAccessToken = () => {
        const tokenOverrides = LocalStorage.get(Constants.TOKEN_OVERRIDES);
        if (tokenOverrides && tokenOverrides[ProviderNames.FACEBOOK]) {
            return tokenOverrides[ProviderNames.FACEBOOK] != undefined;
        }
        return false;
    };

    private bindEvents = () => {
        EventService.subscribe(AppEvents.LOGIN, async (d) => {
            //await this.clearData();
            this.onUserLogin();
        });

        EventService.subscribe(AppEvents.LOGOUT, async (d) => {
            this.killPolling = true;
            await this.clearData();
            await this.logout(false);
            LocalStorage.remove(Constants.USE_MOCK_KEYS);
        });
    };

    // tries to update their token if they're admin.
    public onUserLogin = async () => {
        try {
            log(`onUserLogin`)
            // check if original user and try to update token:
            this.clearLoginFlags();
            let r = await this.getLoginStatus();
            let user = AuthService.getCurrentUser();
            await this.tryUpdateToken(r, user);
        } catch (e) {
            console.warn(`Error onUserLogin`, getInnerError(e));
            throw e;
        }
    };

    // Loads Facebook SDK
    public initialize = (callback) => {
        //let appId = IS_DEV() ? process.env.FB_TEST_APP_ID : process.env.FB_APP_ID;
        const appId = process.env.FB_APP_ID;
        window.fbAsyncInit = function () {
            const cb = callback;
            FB.init({
                appId: appId,
                autoLogAppEvents: true,
                xfbml: true,
                version: `v${process.env.FB_API_VERSION}`
            });
            if (cb) cb();
        };
    };

    public tryUpdateToken = async (r, user) => {
        try {
            log('tryUpdateToken()...');
            if (!user) {
                throw "No user is available to authorize. Please logout and refresh the page.";
            }
            if ((r.status === 'connected' && r.authResponse) && await this.isOriginalUser(r.authResponse, user)) {
                log('Is original user, updating token.', user, r);
                debug(user, "Updating FB token.", { fbAccessToken: r.authResponse.accessToken });
                const tr = await this.getAndSaveLongToken(r.authResponse.accessToken, r.authResponse.userID);
                if (tr) {
                    // get a new user object if the original user was set on the account
                    let authed = await AuthService.checkSession() !== false;
                    if (authed) await AuthService.updateCurrentUser(user);
                    return true;
                }
            } else {
                log(`Could not update facebook, not original FB user.`);
                debug(user, "Could not update facebook, not original FB user.", { authResponse: r?.authResponse });
            }
            return false;
        } catch (e) {
            throw e;
        }
    };

    // Prompts for Facebook login
    public login = async (user) => {
        return new Promise((resolve, reject) => {
            try {
                user = user || AuthService.getCurrentUser();
                log('login', user);

                const _handleLoginResponse = async (r) => {
                    try {
                        return resolve(await this.handleLoginResponse(r, user, true));
                    } catch (e) {
                        return reject(e);
                    }
                };

                FB.login((r) => {
                    _handleLoginResponse(r);
                }, {
                    fallback_redirect_uri: process.env.FB_FALLBACK_REDIRECT_URI || 'https://app.adaya.ai/login',
                    scope: 'ads_read,public_profile'
                });
            } catch (e) {
                reject(e);
            }
        }); F
    };

    // Gets current Facebook user's login status
    public checkLogin = async (user, updateToken = false) => {
        return new Promise(async (resolve, reject) => {
            try {
                user = user || AuthService.getCurrentUser();
                log('checkLogin', user);
                const isOwner = isAccountOwner(user);
                let connected = await this.isConnected();

                // account admin needs to be logged in always:
                if (!connected && isOwner) {
                    log(`Calling assert`, isOwner, user)
                    connected = await this.assertLogin();
                    if (connected) updateToken = true; // signal we should update to a new token
                }

                const _handleCheckLoginResponse = async (r) => {
                    try {
                        return resolve(await this.handleLoginResponse(r, user, updateToken));
                    } catch (e) {
                        return reject(e);
                    }
                };

                FB.getLoginStatus((r) => {
                    log('fb checkLogin response:', r);
                    _handleCheckLoginResponse(r);
                });
            } catch (e) {
                reject(e);
            }
        });
    };

    // does a checkLogin and a login if not connected.
    public assertLogin = async (user) => {
        return new Promise(async (resolve, reject) => {
            log(`assertLogin`)
            try {
                let connected = await this.isConnected();
                if (!connected) {
                    if (this.hasMockAccessToken()) {
                        log(`has mock token, not prompting login.`)
                        connected = true;
                    } else {
                        connected = await this.login(user);
                    }
                }

                if (!connected) {
                    return resolve(false);
                }

                return resolve(true);
            } catch (e) {
                console.log('FB assertlogin error', e);
                return resolve(false);
            }
        });
    };

    public isConnected = async () => {
        try {
            let r = await this.getLoginStatus();
            log(`isConnected?`, r)
            return r?.status == 'connected';
        } catch (e) {
            throw e;
        }
    };

    public getLoginStatus = async () => {
        return new Promise((resolve, reject) => {
            try {
                FB.getLoginStatus(async (response) => {
                    return resolve(response);
                });
            } catch (e) {
                return reject(e);
            }
        });
    };

    // private loginCallbackSuccess = async (r, user) => {
    //     try {
    //         log('login callback success', r, user);
    //         // update the token again if they happened to relogin to facebook somehow (maybe this is redundant?)
    //         if (user) {
    //             log("Trying to update fb token...", user);
    //             if (await this.tryUpdateToken(r, user)) {
    //                 log('Token updated, reloading user...');

    //                 // reload the changed user
    //                 let authed = await AuthService.checkSession();
    //                 if (authed) {
    //                     await AuthService.updateCurrentUser(user);
    //                     return true;
    //                 }
    //                 // some unknown error...
    //                 return false;
    //             } else {
    //                 // not original user? ok.
    //             }
    //         }

    //         // login is ok anyway
    //         return true;
    //     } catch (e) {
    //         throw e;
    //     }
    // };

    // handles the response from both login and checkLogin
    private handleLoginResponse = async (r, user, updateToken = false) => {
        try {
            log(`handleLoginResponse`, r, user)
            if (!r) return false;

            let token = r.authResponse?.accessToken;

            // We consider them an owner if there is no current user (ie. not logged in, for signup)
            const isOwner = (!user) || isAccountOwner(user);

            // update token if original user
            if (r.status === 'connected') {
                if (isOwner) {
                    log(`Owner is connected.`);
                    if (user && updateToken) {
                        log(`Tying to update token.`)
                        token = await this.tryUpdateToken(r, user);
                    } else {
                        log(`No user given (new user?), token is good.`)
                        // store and use the user's token
                        await this.setFacebookAccessToken(token);
                    }
                } else {
                    console.log(`Not account owner.`)
                }
            } else {
                log(`FB user is not connected. Continuing.`, r);
            }

            // try to validate the new or current token.
            if (token) {
                log(`We have a token.`);
                return true;
            }

            if (this.hasMockAccessToken()) {
                token = this.getFacebookAccessToken();
                log(`Has mock access token.`, token);
            } else {
                let fbProvider = getAccountProvider(user?.activeAccount, ProviderNames.FACEBOOK);
                if (fbProvider?.oauth?.token) {
                    token = fbProvider?.oauth?.token;
                    log(`Found account token, using.`)
                }
            }

            // verify the token is still good
            if (token) {
                //let dt = await this.decodeToken(token);
                let isValid = await this.isValidToken(token);
                if (isValid) {
                    log(`Token is valid.`, token);
                    this.setFacebookAccessToken(token);
                    return true;
                } else {
                    log(`Token is not valid.`, token);
                }

                // todo: wrap/handle better.
                let e = new ProviderError(ProviderNames.FACEBOOK, 'Invalid API token.', { data: { error: ErrorMessages.fixApiTokenError(ProviderNames.FACEBOOK) } });
                handleResponseErrorNotifications(e);

                // return false if owner, so it flags system to prompt FB login.
                return isOwner ? false : true;
            }

            log(`No token found, must login.`);
            return false;

        } catch (e) {
            e = getInnerError(e);
            handleResponseErrorNotifications(e);
            if (isInvalidTokenError(e)) return false;
            throw e;
        }
    };

    // // handles the response from both login and checkLogin
    // private handleLoginResponse = async (r, user) => {
    //     try {
    //         if (!r) return resolve(false);
    //         if (r.authResponse && r.status == 'connected') {
    //             return await this.loginCallbackSuccess(r, user);
    //         } else {
    //             if (this.hasMockAccessToken()) {
    //                 log(`FB login failed, using mock token.`)
    //                 return true;
    //             } else {
    //                 log('FB login failed. Using account token.');
    //                 let fbProvider = getAccountProvider(user?.activeAccount, ProviderNames.FACEBOOK);
    //                 if (fbProvider?.oauth?.token) {
    //                     /// todo: do this? or not.
    //                     log(`Using stored account provider token`);
    //                     this.setFacebookAccessToken(fbProvider.oauth.token);
    //                     return true;
    //                 }
    //             }

    //             // user cancelled login or did not fully authorize
    //             return false;
    //         }
    //     } catch (e) {
    //         throw e;
    //     }
    // }

    public isValidToken = async (token) => {
        log(`isValidToken`, token)
        return new Promise(async (resolve, reject) => {
            const tr: any = await ApiService.post('/auth/decode-facebook-token', { token });
            log(`isValidToken response: `, tr)
            if (tr.success && tr.valid) {
                return resolve(true);
            }
            return resolve(false);
        });
    };

    public decodeToken = async (token) => {
        return new Promise((resolve, reject) => {
            try {
                FB.api(
                    `/debug_token?input_token=${token}`,
                    //&access_token=EAAKqVqdnxJcBAOOST1U5jYfg5wkYwfTPifnP8aKFccdP96HZAS3Gb3vES1taG3gqxSJZBZCiU4jgpI1NYvu4F8wpkXvIoyoMcZBFeABd74yecoKpZAhc7MzuZBwmLShOx7ZARrJ7mmhpCweu9zhd147IHjUzhtrYLaaZAd7AZCtMd2OyYeOxFhS7ZB`,
                    function (r) {
                        log(`decode response`, r);
                        if (r?.error || r?.data?.error) {
                            return reject(getInnerError(r?.error || r?.data?.error));
                        }
                        log(`Token is good.`, r.data);
                        return resolve(r.data);
                    }
                );
            } catch (e) {
                throw e;
            }
        });
    };

    /**
     * @description Gets the actual token for the current user, returning undefined if not logged into Facebook.
     */
    public getFacebookRealToken = async () => {
        try {
            let fbAuth = await this.getLoginStatus();
            if (fbAuth.status == 'connected' && fbAuth.authResponse) {
                console.log(`connected...`, fbAuth)
                return fbAuth.authResponse.accessToken;
            }
            return undefined;
        } catch (e) {
            throw e;
        }
    };



    // This checks if the provider has the original FB user ID saved, and confirms if so.
    // If not, it will do a manual check by decoding the existing token with the current.
    // If the user's match, it's the original user (for an old account).
    // The original user should be stored on the server, and token updated.
    public isOriginalUser = async (fbStatus, user) => {
        try {
            log('isOriginalUser?', fbStatus, user);
            user = user || AuthService.getCurrentUser();

            // if no user is given, we assume original (ie. for signup page, etc):
            if (!user) return true;

            let fbProvider = getAccountProvider(user.activeAccount, ProviderNames.FACEBOOK);

            if (fbProvider && fbStatus) {
                if (fbProvider.oauth?.accountUserId) {
                    // if account user id matches current, it's original user.
                    if (fbProvider.oauth.accountUserId == fbStatus.userID) {
                        return true;
                    }
                }

                // try to validate user from token for old accounts that might not have accountUserId set
                if (fbProvider.oauth?.token) {
                    try {
                        log(`Trying to decode account provider token to check original user...`)
                        let dt = await this.decodeToken(fbProvider.oauth?.token);
                        log('decoded fb token:', dt?.data, fbStatus);
                        if (dt?.data?.user_id) {
                            if (dt.data.user_id == fbStatus.userID) {
                                log('Token ID matches, is original user.');
                                return true;
                            }
                        }
                    } catch (e) {
                        console.log('Couldnt decode FB user:', e);
                    }
                }

                // try to validate user from    name:
                log('Trying to manually match user with FB profile', fbStatus);
                let fbProfile = await this.getProfile(fbStatus.userID);
                if (fbProfile) {
                    if (fbProfile.email == user.email || fbProfile.name == user.name) {
                        log(`FB Profile email or name matches. Is orginal user.`, fbProfile, user)
                        return true;
                    }

                    // If those don't match, last fallback is a manual name we can set in our DB,
                    // in rare cases such as old account or wierd names not matching.
                    if (fbProvider.oauth?.accountUserName) {
                        if (fbProvider.oauth.accountUserName == fbProfile.name) {
                            log(`accountUserName matches profile name. Is orginal user.`)
                            return true;
                        }
                    }

                    log(`FB profile is not original user.`);
                }
            }

            return false;
        } catch (e) {
            console.log('Facebook isOriginalUser error', e);
            return false;
        }
    };

    // resets some flags that could have been set during logout, but need to be cleared for a new user:
    public clearLoginFlags = () => {
        this.tooManyCalls = false;
        this.killPolling = false;
    };

    public logout = async (logoutFacebook = true) => {
        return new Promise(async (resolve, reject) => {
            try {
                log('FacebookService.logout');
                // const apiAccessToken = await this.getFacebookAccessToken();
                // if (apiAccessToken) {
                LocalStorage.remove(Constants.FACEBOOK_ACCESS_TOKEN);;
                LocalStorage.remove(Constants.TOKEN_OVERRIDES);
                LocalStorage.remove(Constants.USE_MOCK_KEYS);
                let status = await this.getLoginStatus();
                if (status.status == 'connected' && logoutFacebook) {
                    FB.logout((r) => {
                        return resolve(true);
                    });
                } else {
                    return resolve(true);
                }
            } catch (e) {
                return reject(e);
            }
        });
    };

    public clearData = async () => {
        // todo: clear old facebook data in cache
        await this.store.clear();
        LocalStorage.remove(Constants.FACEBOOK_ACCESS_TOKEN);
        LocalStorage.remove(Constants.TOKEN_OVERRIDES);
        LocalStorage.remove(Constants.USE_MOCK_KEYS);
    };

    public getProfile = async (userId) => {
        return new Promise((resolve, reject) => {
            FB.api(`${userId}?fields=id,name,email`, (r) => {
                if (r && !r.error) {
                    return resolve(r);
                }
                return reject(r?.error);
            });
        });
    };

    // exchanges the given access token to get a long-lived token.
    public getAndSaveLongToken = async (accessToken, accountUserId?) => {
        try {
            const tr: any = await ApiService.post('/auth/get-facebook-long-token', { token: accessToken, accountUserId });
            if (tr.token) {
                log(`setting token ${tr.token}`)
                this.setFacebookAccessToken(tr.token);
                return tr.token;
            } else {
                // just set the current short token for now, but should fix this...
                log(`Couldnt generate long token, setting token to regular: ${accessToken}`)
                this.setFacebookAccessToken(accessToken);
                return accessToken;
            }
        } catch (e) {
            log('long token error', e);
            throw e;
        }
    };

    // These could be moved? ------------------------ or above abstracted to more generic ProviderLoginStrategy or something.

    // Gets the Facebook user's ad accounts
    public getMyAdAccounts = async (fbAccessToken) => {
        return new Promise(async (resolve, reject) => {
            if (this.tooManyCalls) {
                return reject('There have been too many calls, cancelling call. Please wait and refresh the browser to try the calls again.');
            }

            const acountIdCacheKey = 'FB_AD_ACCOUNTS_IDS';
            let results = await this.store.get(acountIdCacheKey);
            if (results) return resolve(results);

            results = [];

            FB.api(
                '/me/adaccounts?fields=id,account_id,name,adspixels{name,id}',
                {
                    access_token: `${fbAccessToken || await this.getFacebookAccessToken()}`
                },
                async (response) => {
                    const getRestOfAdAccounts = async (nextUrl) => {
                        const r = await ApiService.get(nextUrl);
                        if (r.data) {
                            results = results.concat(r.data);
                        }
                        if (r.paging.next) {
                            await getRestOfAdAccounts(r.paging.next);
                        }
                    };

                    if (response && response.data && response.data.length) {
                        results = results.concat(response.data);
                        if (response.paging && response.paging.next) {
                            await getRestOfAdAccounts(response.paging.next);
                        }

                        await this.store.set(acountIdCacheKey, results);
                        return resolve(results);
                    } else if (response.error) {
                        if (isOverLimitError(response.error.message)) {
                            this.tooManyCalls = true;
                        }
                        EventService.dispatch(AppEvents.PROVIDER_API_ERROR, response.error);
                        log('getMyAdAccounts error', response.error);
                        reject(response.error);
                    } else {
                        reject('There was an error with the request');
                    }
                }
            );
        });
    };

    // Retrieves insights data, using cached data if found.
    public getInsightsData = async (
        level,
        accountId,
        dateFrom,
        dateTo,
        timeIncrement,
        limit,
        cache,
        keyLabel = '',
        pollCallback = undefined,
        updateIfCached = false,
        activeCampaignsOnly = false
    ) => {
        const cacheKey = `INSIGHTS_${level}_${keyLabel}_${timeIncrement}` + (limit ? `_${limit}` : '');

        let data = await this.store.get(cacheKey);
        if (!data || !cache || updateIfCached) {
            log('Fetching new insights:', level, keyLabel, cacheKey, dateFrom, dateTo, cache, updateIfCached);

            data = await this.insightsQuery(level, accountId, dateFrom, dateTo, timeIncrement, keyLabel, pollCallback, activeCampaignsOnly);

            //log('Saving new insights: ', level, keyLabel, dateFrom, dateTo, cacheKey, data);

            await this.store.set(cacheKey, data);
        } else {
            //console.log('See cached insights data:', keyLabel, cacheKey, data, cache, updateIfCached);
        }

        // clear cached data for this key from previous days
        await this.store.clearIf((i, key, entry) => {
            return key.indexOf('FB_INSIGHTS_') >= 0 && key.indexOf(keyLabel) >= 0 && key != cacheKey;
        });

        return data;
    };

    // Constructs an insights query and fetches the data.
    public insightsQuery = async (level, accountId, dateFrom, dateTo, timeIncrement, keyLabel = '', pollCallback = undefined, activeCampaignsOnly = false) => {
        const apiAccessToken = await this.getFacebookAccessToken();
        if (!apiAccessToken) {
            console.error('No Facebook access token found for user.');
            return null;
        }

        const fields =
            level === 'campaign'
                ? 'campaign_id,campaign_name,ad_id,ad_name,adset_id,adset_name,spend,ctr,frequency,actions,action_values,unique_actions,purchase_roas,cost_per_action_type,cost_per_unique_action_type,cost_per_conversion,website_ctr'
                : 'spend,ctr,actions,action_values';

        const filtering = [];

        if (level == 'campaign') {
            //filtering.push({ field: 'spend', operator: 'GREATER_THAN', value: 0 });

            //filtering.push({ field: 'campaign.effective_status', operator: 'NOT_IN', value: ['ARCHIVED'] });
            filtering.push({
                field: 'campaign.effective_status',
                operator: 'IN',
                value: ['ACTIVE', 'PAUSED', 'CAMPAIGN_PAUSED', 'IN_PROCESS', 'WITH_ISSUES', 'DELETED', 'ARCHIVED']
            });
            //enum {ACTIVE, PAUSED, DELETED, CAMPAIGN_PAUSED, ARCHIVED, IN_PROCESS, WITH_ISSUES}

            if (activeCampaignsOnly) {
                //console.log('GETTING ACTIVE CAMPAIGNS ONLY', keyLabel, dateFrom, dateTo)
                filtering.push({ field: 'campaign.effective_status', operator: 'IN', value: ['ACTIVE'] });
            } else {
                //console.log('GETTING ALL CAMPAIGNS', keyLabel, dateFrom, dateTo);
            }
        } else {
            //filtering.push({ field: "action_type", operator: "IN", value: ['offsite_conversion.fb_pixel_purchase'] });
        }

        const opts = {
            fields: fields,
            filtering: filtering,
            level: level,
            edges: 'ads',
            access_token: apiAccessToken,
            time_range: { since: dateFrom, until: dateTo },
            time_increment: timeIncrement,
            locale: 'en_US',
            action_attribution_windows: ['1d_view', '7d_click', '28d_click']
        };

        const _combine1dView7dClick = (data) => {
            const _default = [{ value: 0 }];
            data.forEach((d) => {
                d.action_values =
                    (Array.isArray(d.action_values) &&
                        d.action_values.map((av) => {
                            av.value = (parseFloat(av['value'] || 0) + parseFloat(av['1d_view'] || 0)).toFixed(2);
                            return av;
                        })) ||
                    _default;

                d.actions =
                    (Array.isArray(d.actions) &&
                        d.actions.map((a) => {
                            a.value = (parseFloat(a['value'] || 0) + parseFloat(a['1d_view'] || 0)).toFixed(2);
                            return a;
                        })) ||
                    _default;
            });
            return data;
        };

        return new Promise((resolve, reject) => {
            if (this.tooManyCalls) {
                return reject('There have been too many calls, cancelling call. Please wait and refresh the browser to try the calls again.');
            }

            FB.api(
                `/${accountId}/insights`,
                'post', //level === 'campaign' ? 'post' : 'get',
                opts,
                async (response) => {
                    // check errors
                    if (response.error_user_msg) {
                        EventService.dispatch(AppEvents.API_ERROR, response.error_user_msg);
                        return reject(response.error_user_msg);
                    } else if (response.error) {
                        if (isOverLimitError(response.error.message)) {
                            this.tooManyCalls = true;
                        }
                        EventService.dispatch(AppEvents.API_ERROR, response);
                        return reject(response.error);
                    }

                    if (response.report_run_id) {
                        // it's a report, so poll for result:
                        const data = await this._waitForInsightsReport(accountId, response.report_run_id, apiAccessToken, pollCallback);
                        if (data) {
                            const result = _combine1dView7dClick(data);
                            return resolve(result);
                        } else {
                            return resolve(null);
                        }
                    }

                    if (response && response.data) {
                        return resolve(_combine1dView7dClick(response.data));
                    } else {
                        console.log('FB api error', response);
                        return reject('There was an error with the api request.');
                    }
                }
            );
        });
    };

    // Constructs an insights query and fetches the data.
    public campaignsListQuery = async (accountId, dateFrom, dateTo, pollCallback = undefined, useCache = true, keyLabel = 'campaigns-list') => {
        return new Promise(async (resolve, reject) => {
            let cacheKey, data;
            if (useCache) {
                cacheKey = `CAMPAIGNS_LIST_${keyLabel}`;
                data = await this.store.get(cacheKey);
                if (data) {
                    log('CACHED campaignsListQuery', data)
                    return resolve(data);
                }
            }

            const apiAccessToken = await this.getFacebookAccessToken();
            if (!apiAccessToken) {
                console.error('No Facebook access token found for user.');
                return null;
            }

            if (this.tooManyCalls) {
                return reject('There have been too many calls, cancelling call. Please wait and refresh the browser to try the calls again.');
            }

            // get all campaigns, adsets, and ads:

            const getType = (type) => {
                let params = {
                    fields: 'id,name,status',
                    access_token: apiAccessToken,
                    limit: (type == 'ads' ? 10000 : (type == 'adsets' ? 5000 : 1000)),
                    time_range: { since: dateFrom, until: dateTo },
                    effective_status: ['ACTIVE', 'PAUSED']
                };

                return new Promise(async (resolveType, rejectType) => {
                    FB.api(`/${accountId}/${type}`, 'get', params, async (response) => {
                        // check errors
                        if (response.error_user_msg) {
                            EventService.dispatch(AppEvents.PROVIDER_API_ERROR, response.error_user_msg);
                            return rejectType(response.error_user_msg);
                        } else if (response.error) {
                            if (isOverLimitError(response.error.message)) {
                                this.tooManyCalls = true;
                            }
                            EventService.dispatch(AppEvents.PROVIDER_API_ERROR, response);
                            return rejectType(response.error);
                        }

                        const dd = response.report_run_id ?
                            await this._waitForInsightsReport(accountId, response.report_run_id, apiAccessToken, pollCallback) :
                            response.data;

                        if (dd) {
                            console.log('Got FB Campaign data ==>', type, dd)
                            return resolveType(dd)
                        }

                        console.log('FB api error for type:', type, response);
                        return rejectType('There was an error with the api request.');
                    });
                });
            };

            let r = await Promise.all([
                getType('campaigns'),
                getType('adsets'),
                getType('ads')
            ]);

            data = {
                campaigns: r[0],
                adsets: r[1],
                ads: r[2]
            };

            if (useCache) {
                await this.store.set(cacheKey, data);
            }
            log('campaignsListQuery all data:', data);

            return resolve(data);
        });
    };

    private _waitForInsightsReport = async (accountId, reportId, apiAccessToken, pollCallback) => {
        const self = this;

        const waitForResult = async () => {
            return new Promise((resolve, reject) => {
                if (self.tooManyCalls) {
                    return reject('There have been too many calls, cancelling call. Please wait and refresh the browser to try the calls again.');
                }

                if (self.killPolling) {
                    return reject('Polling killed.');
                }

                FB.api(`/${reportId}`, 'get', { access_token: apiAccessToken }, (response) => {
                    if (response) {
                        if (response.id) {
                            if (pollCallback) pollCallback(response);
                            if (response.async_status == 'Job Completed') {
                                return resolve(response);
                            } else if (response.async_status == 'Job Failed') {
                                console.log('Facebook wait for insights job failed:', response);
                                self.killPolling = true;
                                let errMsg = 'Request for Facebook Insights report has failed. Please try refreshing the page.';
                                NotificationService.notifyError('Facebook Error', errMsg);
                                return reject(errMsg);
                            } else if (response.error) {
                                if (isOverLimitError(response.error.message)) {
                                    self.tooManyCalls = true;
                                }
                                return reject(response.error);
                            }
                        } else if (response.error) {
                            log('Ad api error for report', reportId, response.error);
                            return reject(response.error);
                        }
                    } else {
                        return reject('There was an error with the api request.');
                    }

                    // keep waiting
                    setTimeout(async () => {
                        const r = await waitForResult();
                        if (r.async_status == 'Job Completed') {
                            return resolve(r);
                        } else {
                            console.error('still waiting? TODO: handle this state?', r);
                        }
                    }, POLL_TIME_MS);
                });
            });
        };

        return new Promise(async (resolve, reject) => {
            if (this.tooManyCalls) {
                return reject('There have been too many calls, cancelling call. Please wait and refresh the browser to try the calls again.');
            }

            await waitForResult();

            // can probaly combine this with the above method..
            FB.api(`/${reportId}/insights`, 'get', { access_token: apiAccessToken }, async (response) => {
                const data: Array<any> = [];
                let next = '';
                if (response && response.data) {
                    data.push(...response.data);
                    next = response.paging.next;
                    while (next) {
                        const res = await (await fetch(next)).json();
                        if (res.paging) {
                            next = res.paging.next;
                        }
                        data.push(...res.data);
                    }
                    return resolve(data);
                } else if (response.error) {
                    console.log('Ad api error for report', reportId, response.error);
                    reject(response.error);
                } else {
                    reject('There was an error with the api request');
                }
            });
        });
    };

    // Makes a request for: list of account adsets, list of account ads, and makes a separate request
    // for all adset->ad insights for each adset (if it's campaign has > $0 adspend)
    public getAdInsights = async (accountId, campaignData, dateFrom, dateTo, keyLabel = '', cache = true, updateIfCached = false) => {
        const self = this;
        const adsetsCacheKey = `ADSETS_${keyLabel}`;
        const adsCacheKey = `ADS_${keyLabel}`;

        log('getAdInsights', keyLabel, campaignData, cache, updateIfCached);

        if (!campaignData) {
            console.log('Error: no campaign data passed to getAdsets, cannot proceed with retrieving campaign insights.');
            return undefined;
        }

        if (this.tooManyCalls) {
            return undefined;
        }

        // adsets can be cached...
        const adsets = await this.store.get(adsetsCacheKey);
        if (adsets && cache && updateIfCached) {
            // let it attempt for fresh adset data later...
        }

        const apiAccessToken = await this.getFacebookAccessToken();
        if (!apiAccessToken) {
            console.error('No Facebook access token found for user.');
            return null;
        }

        const adsetIds = [],
            ads = [];
        const campaignInsights = {},
            adInsights = {};

        if (!self.tooManyCalls) {
            await Promise.all(
                // get all insights for the given "real" campaigns (only ones with spend > 0, and not archived)...

                campaignData.map((campaign) => {
                    return new Promise(async (resolve, reject) => {
                        if (!campaign.campaign_id) {
                            console.log('Campaign ID does not exist on campaign object, cannot retrieve insights.', campaign, keyLabel);
                            return reject('Campaign ID does not exist on campaign object, cannot retrieve insights.');
                        }
                        // if we have this adset already, resolve:
                        const campaignInsightCacheKey = `INSIGHTS_CAMPAIGN_${campaign.campaign_id}_${keyLabel}`;

                        const cachedInsights = await this.store.get(campaignInsightCacheKey);
                        if (cachedInsights && cache && !updateIfCached) {
                            campaignInsights[campaign.campaign_id] = cachedInsights;
                            cachedInsights.forEach((ai) => {
                                adInsights[ai.ad_id] = ai;
                            });

                            //console.log('using cached insights', cachedInsights, adsetInsightCacheKey)
                            return resolve(cachedInsights);
                        }

                        if (this.tooManyCalls) {
                            return reject('There have been too many calls, cancelling call. Please wait and refresh the browser to try the calls again.');
                        }

                        const opts = {
                            access_token: apiAccessToken,
                            time_range: { since: dateFrom, until: dateTo },
                            locale: 'en_US',
                            fields: 'campaign_name,campaign_id,adset_name,adset_id,ad_id,ad_name,spend',
                            summary: ['spend'],
                            level: 'ad',
                            limit: 1000,
                            edges: 'adset',
                            status: ['ACTIVE', 'PAUSED', 'DELETED', 'ARCHIVED'],
                            effective_status: ['ACTIVE', 'PAUSED', 'CAMPAIGN_PAUSED', 'IN_PROCESS', 'WITH_ISSUES', 'DELETED', 'ARCHIVED']
                        };

                        log('Fetching new campaign insights:', campaignInsightCacheKey, campaign.campaign_name);

                        FB.api(`/${campaign.campaign_id}/insights`, 'get', opts, async (response) => {
                            if (response.error) {
                                if (isOverLimitError(response.error.message)) {
                                    this.tooManyCalls = true;
                                }

                                EventService.dispatch(AppEvents.API_ERROR, response);
                                return reject(response.error);
                            }

                            if (response && response.data) {
                                //console.log('Saving new campaign insights:', cacheKey, campaign, insights);
                                await this.store.set(campaignInsightCacheKey, response.data);
                                campaignInsights[campaign.campaign_id] = response.data;
                                response.data.forEach((ai) => {
                                    adInsights[ai.ad_id] = ai;
                                });
                                return resolve(response.data);
                            } else {
                                console.log('FB api error:', response);
                                return reject('There was an error with the api request.');
                            }
                        });
                    });
                })
            );
        }

        const r = { adInsights: adInsights, campaignInsights: campaignInsights };
        return r;
    };

    /**
     * @description Retrieves the currently stored FB access token for the local uuuser.
     * The token stored may be from another account (if mocking the account).
     * If this mock token is not desired, pass noOverride=true.
     * @param {boolean} [noOverride=false]
     */

    public getFacebookAccessToken = (noOverride = false) => {
        if (noOverride) {
            return LocalStorage.get(Constants.FACEBOOK_ACCESS_TOKEN);
        }

        if (this.hasMockAccessToken()) {
            const tokenOverrides = LocalStorage.get(Constants.TOKEN_OVERRIDES);
            if (tokenOverrides && tokenOverrides['Facebook']) {
                return tokenOverrides['Facebook'];
            }
            return process.env.FB_MOCK_ACCESS_TOKEN;
        }

        return LocalStorage.get(Constants.FACEBOOK_ACCESS_TOKEN);
    };

    // public getFacebookAccessToken = async (noOverride = false) => {
    //     let token = LocalStorage.get(Constants.FACEBOOK_ACCESS_TOKEN);
    //     if (noOverride) {
    //         // if we want the real/current user token, but it doeesn't exist for some reason,
    //         // do a check to see if they're connected and set the token for them:
    //         if (!token) {
    //             token = await this.getFacebookRealToken();
    //             console.log(`no token, trying to get:`, token)
    //             if (token) {
    //                 LocalStorage.set(Constants.FACEBOOK_ACCESS_TOKEN, token);
    //             }
    //         }
    //     } else {
    //         if (this.hasMockAccessToken()) {
    //             console.log(`using mock.`)
    //             const tokenOverrides = LocalStorage.get(Constants.TOKEN_OVERRIDES);
    //             if (tokenOverrides && tokenOverrides['Facebook']) {
    //                 token = tokenOverrides['Facebook'];
    //             } else {
    //                 //throw "Could not retriev an access token for Facebook."
    //                 //token = process.env.FB_MOCK_ACCESS_TOKEN; // fallback to master token
    //             }
    //         }
    //     }
    //     return token;
    // };

    public getFacebookRealUser = () => {
        const tokenOverrides = LocalStorage.get(Constants.TOKEN_OVERRIDES);
        if (tokenOverrides && tokenOverrides['Facebook']) {
            return tokenOverrides['Facebook'];
        }

        const token = this.hasMockAccessToken() ? process.env.FB_MOCK_ACCESS_TOKEN : LocalStorage.get(Constants.FACEBOOK_ACCESS_TOKEN);

        return token;
    };

    public getAccountMetadata = (accountId): Promise<any> => {
        accountId = accountId.indexOf('act_') === 0 ? accountId : `act_${accountId}`;
        return new Promise(async (resolve, reject) => {
            FB.api(
                `/${accountId}`,
                {
                    access_token: `${await this.getFacebookAccessToken()}`,
                    fields: 'currency'
                },
                (response) => {
                    if (response.error) {
                        return reject(response.error);
                    }
                    return resolve(response);
                }
            );
        });
    };

    public setFacebookAccessToken = (accessToken) => {
        LocalStorage.set(Constants.FACEBOOK_ACCESS_TOKEN, accessToken);
    };

    public removeDataSources = async (dataSourceIds, emitEvent = true) => {
        await Promise.all(
            dataSourceIds.map((id) => {
                return new Promise(async (resolve, reject) => {
                    console.log('Removing data source:', id);
                    await this.store.remove(id);
                    return resolve(true);
                });
            })
        );

        if (emitEvent) {
            EventService.dispatch(AppEvents.DATA_SOURCE_REMOVED, dataSourceIds);
        }
    };
}

export default new FacebookService();